import { CopyIcon } from "@phosphor-icons/react";
import { App, Button, Tooltip } from "antd";
import classNames from "classnames";
import { useCallback, useState } from "react";
type IconVisibility = "always" | "hover";
export interface CopyTextProps {
  value: string;
  className?: string;
  iconVisibility?: IconVisibility;
  tooltip?: string;
  showFeedback?: boolean;
  iconSize?: number;
  iconWeight?: "bold" | "regular" | "light" | "thin" | "fill";
  onCopy?: (value: string) => void;
}

/**
 * Displays text with a copy-to-clipboard icon.
 * The copy icon can be always visible or only appear on hover.
 */
export const CopyText = ({
  value,
  className,
  iconVisibility = "hover",
  tooltip = "Copy",
  showFeedback = true,
  iconSize = 16,
  iconWeight = "bold",
  onCopy
}: CopyTextProps) => {
  const [isCopying, setIsCopying] = useState(false);
  const {
    message
  } = App.useApp();
  const handleCopy = useCallback(async () => {
    if (isCopying) {
      return;
    }
    setIsCopying(true);
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = value;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      if (showFeedback) {
        message?.success?.("Copied");
      }
      onCopy?.(value);
    } catch {
      if (showFeedback) {
        message?.error?.("Copy failed");
      }
    } finally {
      setIsCopying(false);
    }
  }, [value, showFeedback, onCopy, isCopying]);
  const iconClasses = classNames("ml-1 flex rounded items-center p-1 transition-colors hover:bg-gray-100 aspect-square", iconVisibility === "hover" ? "opacity-0 group-hover:opacity-100 transition-opacity" : "opacity-100");
  return <div className={classNames("inline-flex items-center max-w-full group", className)} data-sentry-component="CopyText" data-sentry-source-file="CopyText.tsx">
      <span className="truncate">{value}</span>
      <Tooltip title={tooltip} data-sentry-element="Tooltip" data-sentry-source-file="CopyText.tsx">
        <Button size="small" type="text" aria-label="Copy to clipboard" className={iconClasses} onClick={handleCopy} disabled={isCopying} icon={<CopyIcon size={iconSize} weight={iconWeight} />} data-sentry-element="Button" data-sentry-source-file="CopyText.tsx" />
      </Tooltip>
    </div>;
};